import React, { useState, useEffect } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Typography,
  Pagination,
  Button,
  Grid,
  useTheme,
} from "@mui/material";
import formatCoinName from "src/utils/formatCoinName";
import {
  CurrencyConfigRes,
  useDeletePriceAlertMutation,
  useGetPriceAlertsQuery,
} from "src/store/features/api/generated";
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";
import { AlertTableStyles } from "./styles/AlertsTable.styles";
import EditNotificationsIcon from "@mui/icons-material/EditNotifications";
import { trackEvent } from "src/Amplitude";
import PriceAlertEditModel from "../PriceAlerts/PriceAlertEditModel";
import { useAppDispatch } from "src/store/store";
import { togglePriceAlertEditModal } from "src/store/features/PriceAlerts.slice";
import { PriceAlertsTableSkelton } from "src/components/common/Skeletons";
import BinNoLid from "src/icons/BinIconNoLid";
import BinLid from "src/icons/BinLid";
import NotificationAddIcon from "@mui/icons-material/NotificationAdd";

interface SelectedCoinType {
  price?: number;
  currencyConfig?: CurrencyConfigRes;
  subtitle?: string;
  crossOverPrice?: number;
  logoUrl?: string;
  id?: string;
}

type PaginationParams = { limit: number; page: number };
const AlertsTable = () => {
  const theme = useTheme();

  // -----------------State---------------------
  const [deleteAlert, { isLoading: deletingAlert }] = useDeletePriceAlertMutation();
  const [deleteRefresh, setDeleteRefetch] = useState(false);
  const [editRefresh, setEditRefetch] = useState(false);
  const [paginationParams, setPaginationParams] = useState<PaginationParams>({
    limit: 10,
    page: 1,
  });
  const [selected, setSelected] = useState<SelectedCoinType>({});

  // -----------------Redux---------------------
  const classes = AlertTableStyles();
  const dispatchRedux = useAppDispatch();
  const { data, refetch, isFetching } = useGetPriceAlertsQuery(paginationParams);

  const handlePriceAlert = () => {
    dispatchRedux(togglePriceAlertEditModal());
    //resets RTKQ state
  };
  // -----------------Handlers---------------------
  // Set the selected row data
  const handlePriceAlertEditData = (row: any) => {
    trackEvent(
      `Set Price Alert`,
      {
        ...row.currencyConfig,
      },
      "Price Alerts",
    );
    setSelected({
      price: row.currentPrice,
      currencyConfig: row.currencyConfig,
      subtitle: row.currencyConfig.formatName,
      logoUrl: row.currencyConfig.logoUrl,
      crossOverPrice: row.crossOverPrice,
      id: row.id,
    });
  };
  const handlePagination = (event: unknown, newPage: number): void => {
    setPaginationParams((prev: PaginationParams): PaginationParams => ({ ...prev, page: newPage }));
  };

  // formats the diff percentage color
  const FormatPercentage = (percentage: number) => {
    if (percentage > 0) {
      return <Typography sx={{ color: "green" }} translate="no">{`${percentage}%`}</Typography>;
    } else {
      return <Typography sx={{ color: "red" }} translate="no">{`${percentage}%`}</Typography>;
    }
  };

  // -----------------EFFECTS---------------------
  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (deleteRefresh) {
      // Wait for 300ms before refetching data
      // this is to ensure that the new data is returned from the mongo db and
      //removing this delay will cause the old data to be populated on reload
      const awaitTimeout = (delay: number) => new Promise((resolve) => setTimeout(resolve, delay));
      awaitTimeout(300)
        .then(() => refetch())
        .catch((error) => console.log("Cancel Request Error"));
    }
    if (editRefresh) {
      const awaitTimeout = (delay: number) => new Promise((resolve) => setTimeout(resolve, delay));
      awaitTimeout(300)
        .then(() => refetch())
        .catch((error) => console.log("Edit Request Error"));
    }
    return () => {
      setDeleteRefetch(false);
      setEditRefetch(false);
    };
  }, [deleteRefresh, editRefresh]);

  // -----------------LOADING & NO DATA---------------------
  if (deletingAlert) {
    return <PriceAlertsTableSkelton />;
  }
  // Loading Table State
  if (!data || isFetching) {
    return <PriceAlertsTableSkelton />;
  }

  return (
    <>
      <PriceAlertEditModel
        refetchData={setEditRefetch}
        price={selected.price}
        currencyConfig={selected.currencyConfig}
        subtitle={selected.subtitle}
        logoUrl={selected.logoUrl}
        crossOverPrice={selected.crossOverPrice}
        id={selected.id}
      />
      <TableContainer>
        <Table
          sx={{
            minWidth: 650,
            mb: 5,
            fontWeight: 600,
          }}
          aria-label="price alerts table">
          <TableHead>
            <TableRow>
              <TableCell>Coin</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Price Alert</TableCell>
              <TableCell>Current Price</TableCell>
              <TableCell>Difference</TableCell>
              <TableCell align="center">Edit</TableCell>
              <TableCell align="center">Delete</TableCell>
            </TableRow>
          </TableHead>

          <TableBody sx={{ minHeight: "100%" }}>
            {data?.data.map((row: any) => (
              <TableRow
                key={row.id}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                }}>
                <TableCell
                  style={{
                    position: "sticky",
                    left: "0px",
                    width: "10px",
                  }}>
                  <img
                    src={row.currencyConfig.logoUrl}
                    alt={`${row.currencyConfig.formatName} icon`}
                    width={24}
                    height={24}
                    style={{ borderRadius: "50%" }}
                  />
                </TableCell>
                <TableCell component="th" scope="row">
                  <Stack>
                    <Typography sx={{ fontWeight: 600 }}>
                      {formatCoinName(row.currencyConfig.name)}
                    </Typography>
                    <Typography>{row.currencyConfig.code}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography translate="no">
                    {formatCurrency(row.crossOverPrice, "USD")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography translate="no">{formatCurrency(row.currentPrice, "USD")}</Typography>
                </TableCell>
                <TableCell>{FormatPercentage(row.percentageDifference)}</TableCell>
                <TableCell align="center">
                  <Button
                    disableRipple
                    sx={{
                      "&:hover": {
                        background: "transparent",
                      },
                    }}
                    onClick={() => {
                      handlePriceAlert();
                      trackEvent(
                        `Open Edit Modal`,
                        {
                          token: row.currencyConfig.code,
                        },
                        "Price Alerts",
                      );
                      handlePriceAlertEditData(row);
                    }}>
                    <EditNotificationsIcon />
                  </Button>
                </TableCell>
                <TableCell align="center">
                  <Button
                    disableRipple
                    sx={{
                      "&:hover": {
                        background: "transparent",
                      },
                    }}
                    onClick={() => {
                      deleteAlert({
                        priceAlertIdDto: { id: row.id },
                      });
                      setDeleteRefetch(true);
                      trackEvent(
                        `Deleted Alert`,
                        {
                          ...row.currencyConfig,
                        },
                        "Price Alerts",
                      );
                    }}
                    className={classes.deleteButton}>
                    <Stack>
                      <span className={classes.binLid}>
                        <BinLid />
                      </span>
                      <BinNoLid />
                    </Stack>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {data.totalPriceAlerts === 0 && (
          <Typography sx={{ textAlign: "center", mb: 30 }}>
            You have no active price alerts. Click the
            <NotificationAddIcon
              className={classes.notificationIcon}
              sx={{
                width: "25px",
                height: "25px",
                color: theme.palette.primary.dark,
              }}
            />
            on the markets or coin page to add a price alert
          </Typography>
        )}
      </TableContainer>
      <Grid container alignItems="center" justifyContent={"center"} item>
        <Pagination
          count={data?.totalPages}
          page={data?.currentPage}
          onChange={handlePagination}
          color="secondary"
          showFirstButton
          showLastButton
          sx={{ mb: 3 }}
        />
      </Grid>
    </>
  );
};

export default AlertsTable;
